import React, { useEffect, useState } from 'react';
import { Table, Button, Container, Form } from 'react-bootstrap';
import axios from 'axios';

const ItemManager = () => {
  const [breadData, setBreadData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);
  const API_BASE = process.env.REACT_APP_API_BASE_URL;
  const token = localStorage.getItem("admin_token");
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(`${API_BASE}/api/items_a/items`, { headers });

        const transformed = res.data.map(bread => ({
          bread_type: bread.bread_type,
          sizes: Object.fromEntries(
            Object.entries(bread.sizes).map(([size, price]) => [
              size,
              { current: price, modified: price.toString() }
            ])
          )
        }));

        setBreadData(transformed);
        setLoading(false);
      } catch (err) {
        console.error('Failed to fetch bread data', err);
        setMessage({ type: 'danger', text: 'Failed to load data.' });
        setLoading(false);
      }
    };

    fetchData();
  }, [API_BASE]);

  const handleInputChange = (breadIndex, size, value) => {
    const updated = [...breadData];
    const newBread = { ...updated[breadIndex] };
    const newSizes = { ...newBread.sizes };

    const newValue = value === '' ? '' : value;

    newSizes[size] = {
      ...newSizes[size],
      modified: newValue
    };

    newBread.sizes = newSizes;
    updated[breadIndex] = newBread;

    setBreadData(updated);
  };

  const hasInvalidInputs = () => {
    return breadData.some(bread =>
      Object.values(bread.sizes).some(size =>
        size.modified === '' || parseFloat(size.modified) === 0
      )
    );
  };

  const handleUpdate = async () => {
    if (hasInvalidInputs()) {
      setMessage({ type: 'danger', text: 'Please fill all prices and ensure none are 0.' });
      return;
    }

    try {
      const payload = breadData.map(bread => ({
        bread_type: bread.bread_type,
        sizes: Object.fromEntries(
          Object.entries(bread.sizes).map(([size, obj]) => [
            size,
            parseFloat(obj.modified)
          ])
        )
      }));

      await axios.put(`${API_BASE}/api/items_a/update`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setMessage({ type: 'success', text: 'Bread prices updated successfully!' });
      setTimeout(() => setMessage(null), 4000);
    } catch (err) {
      console.error("Update failed", err);
      setMessage({ type: 'danger', text: 'Failed to update bread prices.' });
    }
  };

  const sizeRange = Array.from(
    new Set(
      breadData.flatMap(bread =>
        Object.keys(bread.sizes).map(size => parseInt(size))
      )
    )
  ).sort((a, b) => a - b);

  return (
    <Container>
      <h3 className="my-3">Manage Bread Prices</h3>

      {message && (
        <div className="mb-3">
          <div className="card">
            <div className="card-body p-2">
              <div className={`alert alert-${message.type} mb-0`} role="alert">
                {message.text}
              </div>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <p>Loading...</p>
      ) : (
        <>
          <Table bordered responsive>
            <thead>
              <tr>
                <th>Bread Type</th>
                {sizeRange.map(size => (
                  <th key={size}>{size}"</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {breadData.map((bread, i) => (
                <tr key={bread.bread_type}>
                  <td><strong>{bread.bread_type}</strong></td>
                  {sizeRange.map(size => {
                    const value = bread.sizes[size]?.modified ?? '';
                    const isInvalid = value === '' || parseFloat(value) === 0;

                    return (
                      <td key={size}>
                        <div style={{ fontSize: "0.75rem", color: "#888" }}>
                          Current: {bread.sizes[size]?.current ?? '-'}
                        </div>
                        <Form.Control
                          type="number"
                          step="any"
                          value={value}
                          isInvalid={isInvalid}
                          onChange={(e) =>
                            handleInputChange(i, size, e.target.value)
                          }
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </Table>

          <div className="text-end">
            <Button
              variant="primary"
              onClick={handleUpdate}
              disabled={hasInvalidInputs()} // Optional: disable button too
            >
              Update Prices
            </Button>
          </div>
        </>
      )}
    </Container>
  );
};

export default ItemManager;
